<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

const { t } = useI18n();
const { localeTag } = useDateLocale();

const props = defineProps({
  report: { type: Object, default: null },
  loading: { type: Boolean, default: false },
});

const numberFormatter = computed(() => new Intl.NumberFormat(localeTag.value));

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const cards = computed(() => [
  {
    key: 'replies',
    label: t('FLOW_BUILDER.REPORTS.ENGAGEMENT.REPLIES'),
    value: numberFormatter.value.format(props.report?.engagement?.replies ?? 0),
    description: t('FLOW_BUILDER.REPORTS.ENGAGEMENT.REPLIES_DESC'),
  },
  {
    key: 'reactions',
    label: t('FLOW_BUILDER.REPORTS.ENGAGEMENT.REACTIONS'),
    value: numberFormatter.value.format(props.report?.engagement?.reactions ?? 0),
    description: t('FLOW_BUILDER.REPORTS.ENGAGEMENT.REACTIONS_DESC'),
  },
  {
    key: 'avg_time',
    label: t('FLOW_BUILDER.REPORTS.ENGAGEMENT.TIME_TO_CONVERSION'),
    value: formatDuration(props.report?.avg_time_to_conversion_seconds),
    description: t('FLOW_BUILDER.REPORTS.ENGAGEMENT.TIME_TO_CONVERSION_DESC'),
  },
]);
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <template v-if="loading">
        <div
          v-for="n in 3"
          :key="`engagement-skeleton-${n}`"
          class="flex flex-col gap-2 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4 animate-pulse"
        >
          <div class="h-3 w-24 rounded bg-n-alpha-2" />
          <div class="h-7 w-20 rounded bg-n-alpha-2 mt-0.5" />
          <div class="h-3 w-40 rounded bg-n-alpha-2 mt-1" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="card in cards"
          :key="card.key"
          class="flex flex-col gap-1 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4"
        >
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ card.label }}
          </span>
          <span class="text-2xl font-semibold text-n-slate-12 tabular-nums">
            {{ card.value }}
          </span>
          <span class="text-xs text-n-slate-10">{{ card.description }}</span>
        </div>
      </template>
    </div>
  </div>
</template>
